function toStr(v) { return (v?._id || v || '').toString(); }

/** Position type 2 = Betreuer (see Positions model). */
export function isBetreuerEntry(entry) {
    const pos = entry?.plid?.posid;
    return !!(pos && typeof pos === 'object' && pos.type === 2);
}

export function isMatchPlayerEntry(entry) {
    return !!(entry?.matchPlayerId || entry?.matchPlayer?._id);
}

export function lineupPlayerKey(entry) {
    if (!entry) return '';
    const mpId = toStr(entry.matchPlayerId?._id || entry.matchPlayerId || entry.matchPlayer?._id);
    if (mpId) return `mp:${mpId}`;
    return `pl:${toStr(entry.plid?._id || entry.plid)}`;
}

export function rosterEntryKey(entry) {
    if (!entry) return '';
    if (entry.source === 'matchplayer' || entry.matchPlayerId) {
        return `mp:${toStr(entry.matchPlayerId?._id || entry.matchPlayerId)}`;
    }
    return `pl:${toStr(entry.plid?._id || entry.plid)}`;
}

export function getLineupPlayerProfile(entry) {
    if (!entry) return { prename: '', surname: '', num: null };
    if (entry.matchPlayerId && typeof entry.matchPlayerId === 'object') {
        return {
            prename: entry.matchPlayerId.prename || '',
            surname: entry.matchPlayerId.surname || '',
            num: entry.num ?? entry.matchPlayerId.num ?? null,
        };
    }
    if (entry.matchPlayer) {
        return {
            prename: entry.matchPlayer.prename || '',
            surname: entry.matchPlayer.surname || '',
            num: entry.num ?? entry.matchPlayer.num ?? null,
        };
    }
    return {
        prename: entry.plid?.prename || '',
        surname: entry.plid?.surname || '',
        num: entry.num ?? null,
    };
}

export function playerDisplayNameFromEntry(entry) {
    const profile = getLineupPlayerProfile(entry);
    const pre = profile.prename ? `${profile.prename[0]}. ` : '';
    return `${pre}${profile.surname}`.trim() || '?';
}

export function matchPlayerToRosterEntry(mp, tid) {
    return {
        source: 'matchplayer',
        tid: toStr(tid || mp.tid),
        num: mp.num,
        matchPlayerId: mp._id,
        plid: {
            _id: mp._id,
            prename: mp.prename,
            surname: mp.surname,
        },
    };
}

export function parsePlayerKey(key) {
    const value = String(key || '');
    if (value.startsWith('mp:')) return { type: 'matchplayer', id: value.slice(3) };
    if (value.startsWith('pl:')) return { type: 'player', id: value.slice(3) };
    return { type: 'player', id: value };
}

export function isStarterEntry(entry) {
    if (entry.isStarter === true) return true;
    if (entry.isStarter === false) return false;
    return entry.type !== 1;
}

export function getTeamLineup(lineup, teamId) {
    return (lineup || []).filter((entry) => toStr(entry.tid) === toStr(teamId));
}

export function getOnFieldPlayers(lineup, teamId, subbedOutIds = []) {
    const outSet = new Set(subbedOutIds.map(toStr));
    return getTeamLineup(lineup, teamId).filter(
        (entry) => isStarterEntry(entry) && !outSet.has(lineupPlayerKey(entry))
    );
}

export function getBenchPlayers(lineup, teamId, subbedInIds = []) {
    const inSet = new Set(subbedInIds.map(toStr));
    return getTeamLineup(lineup, teamId).filter(
        (entry) => !isStarterEntry(entry) && !inSet.has(lineupPlayerKey(entry))
    );
}

export function playerLabel(entry, num = '') {
    const profile = getLineupPlayerProfile(entry);
    const pre = profile.prename ? `${profile.prename[0]}. ` : '';
    const jersey = num || profile.num || entry.num || '';
    return `${jersey ? `#${jersey} ` : ''}${pre}${profile.surname}`.trim();
}

export function playerDisplayName(entry) {
    return playerDisplayNameFromEntry(entry);
}

export function applySubstitutionToLineup(lineup, playerOutKey, playerInKey) {
    const outKey = toStr(playerOutKey);
    const inKey = toStr(playerInKey);
    return lineup.map((entry) => {
        const key = lineupPlayerKey(entry);
        if (key === outKey || key === `pl:${outKey}` || key === `mp:${outKey}`) {
            return { ...entry, type: 1, isStarter: false };
        }
        if (key === inKey || key === `pl:${inKey}` || key === `mp:${inKey}`) {
            return { ...entry, type: 0, isStarter: true };
        }
        return entry;
    });
}

export function revertSubstitutionFromLineup(lineup, playerOutKey, playerInKey) {
    const outKey = toStr(playerOutKey);
    const inKey = toStr(playerInKey);
    return lineup.map((entry) => {
        const key = lineupPlayerKey(entry);
        if (key === outKey || key === `pl:${outKey}` || key === `mp:${outKey}`) {
            return { ...entry, type: 0, isStarter: true };
        }
        if (key === inKey || key === `pl:${inKey}` || key === `mp:${inKey}`) {
            return { ...entry, type: 1, isStarter: false };
        }
        return entry;
    });
}

export function getTickerEventPlayerKey(event) {
    const mp = toStr(event?.matchPlayerId?._id || event?.matchPlayerId);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event?.plid?._id || event?.plid);
    return pl ? `pl:${pl}` : '';
}

export function getTickerEventPlayerOutKey(event) {
    const mp = toStr(event?.matchPlayerOut?._id || event?.matchPlayerOut);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event?.playerOut?._id || event?.playerOut);
    if (pl) return `pl:${pl}`;
    return getTickerEventPlayerKey(event);
}

export function getTickerEventPlayerInKey(event) {
    const mp = toStr(event?.matchPlayerIn?._id || event?.matchPlayerIn);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event?.playerIn?._id || event?.playerIn);
    return pl ? `pl:${pl}` : '';
}

function eventPlayerOutKey(event) {
    const mp = toStr(event.matchPlayerOut?._id || event.matchPlayerOut);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event.playerOut?._id || event.playerOut || event.plid?._id || event.plid);
    return pl ? `pl:${pl}` : '';
}

function eventPlayerInKey(event) {
    const mp = toStr(event.matchPlayerIn?._id || event.matchPlayerIn);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event.playerIn?._id || event.playerIn);
    return pl ? `pl:${pl}` : '';
}

export function getSubbedOutIds(events, teamId) {
    return (events || [])
        .filter((event) => event.type === 5 && toStr(event.tid) === toStr(teamId))
        .map(eventPlayerOutKey)
        .filter(Boolean);
}

export function getSubbedInIds(events, teamId) {
    return (events || [])
        .filter((event) => event.type === 5 && toStr(event.tid) === toStr(teamId))
        .map(eventPlayerInKey)
        .filter(Boolean);
}

export function buildTickerPlayerPayload(key) {
    const parsed = parsePlayerKey(key);
    if (parsed.type === 'matchplayer') {
        return { matchPlayerId: parsed.id, plid: null };
    }
    return { plid: parsed.id, matchPlayerId: null };
}

export function buildTickerSubPayload(outKey, inKey) {
    const out = parsePlayerKey(outKey);
    const inn = parsePlayerKey(inKey);
    const payload = {};
    if (out.type === 'matchplayer') {
        payload.matchPlayerOut = out.id;
        payload.matchPlayerId = out.id;
        payload.plid = null;
        payload.playerOut = null;
    } else {
        payload.playerOut = out.id;
        payload.plid = out.id;
        payload.matchPlayerOut = null;
        payload.matchPlayerId = null;
    }
    if (inn.type === 'matchplayer') {
        payload.matchPlayerIn = inn.id;
        payload.playerIn = null;
    } else {
        payload.playerIn = inn.id;
        payload.matchPlayerIn = null;
    }
    return payload;
}

export { toStr };
